/**
 * Auth state hook — tracks the current user, loading state, and provides
 * login/logout actions. All components consume this via context; the hook
 * itself is the source of truth.
 */

import { useState, useEffect, useCallback } from 'react';
import { getMe, login as apiLogin, logout as apiLogout } from '../lib/api.js';
import type { ApiUser, UserRole } from '@contactrate/shared';

export interface AuthState {
  user: ApiUser | null;
  loading: boolean;
  login: (email: string, password: string, requiredRole?: UserRole) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMe()
      .then(setUser)
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string, requiredRole?: UserRole) => {
    const result = await apiLogin(email, password);
    if (requiredRole !== undefined && result.role !== requiredRole) {
      // Best-effort session invalidation — swallow any network error so the
      // portal-access error ALWAYS fires and setUser is never called.
      try { await apiLogout(); } catch { /* best-effort */ }
      throw new Error('This portal is for operators only.');
    }
    setUser(result);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      // Always clear local state, even if the server call rejects (network blip).
      // Callers should wrap in try/catch if they need to react to the failure;
      // local user state is guaranteed to be null after this resolves or rejects.
      setUser(null);
    }
  }, []);

  return { user, loading, login, logout };
}
